import appConfig from './env';

/**
 * Branding profile for the build (SPX-6494), aligned with the platform-wide
 * `BANNER_BRANDING=specterx|unbranded` convention (SPX-6532).
 *
 * - `specterx` — default SpecterX branding (backward-compatible fallback).
 * - `unbranded` — every SpecterX logo and user-visible "SpecterX" text is
 *   neutralized across all panes and the recipient secure-link experience.
 *   No replacement brand is introduced; tenant logos configured per stage
 *   (COMPANY_LOGO / HEADER_*) are still shown.
 */
export type Branding = 'specterx' | 'unbranded';

interface BrandingProfile {
    /** User-visible product name (browser tab title, in-app copy). */
    productName: string;
}

const BRANDING_PROFILES: Record<Branding, BrandingProfile> = {
    specterx: { productName: 'SpecterX' },
    unbranded: { productName: 'Secure File Protection' },
};

/**
 * Resolved branding for this build: the `VITE_BRANDING` env var (local
 * testing) takes precedence over the per-stage `BRANDING` env-config field
 * (e.g. prod-sicpa). Any other or missing value falls back to `specterx`.
 */
export const BRANDING: Branding = (
    (import.meta.env.VITE_BRANDING ?? appConfig.BRANDING) === 'unbranded' ? 'unbranded' : 'specterx'
);

/** Derived per-branding values for the resolved {@link BRANDING}. */
export const BRANDING_PROFILE: BrandingProfile = BRANDING_PROFILES[BRANDING];

/** Whether SpecterX logos and "SpecterX" copy may be rendered in this build. */
export const IS_BRANDED = BRANDING === 'specterx';

/**
 * Neutral mark shown wherever the SpecterX logo would be in unbranded builds,
 * including the browser tab favicon.
 */
export const NEUTRAL_LOGO_URL = '/neutral-favicon.png';
